/**
 * "Ignore" / undo on a reclaim row's asset suggestion (DAT-9005).
 *
 * Keyed on the (opportunity, asset) PAIR: a reclaim row can carry several suggestions, and
 * dismissing one must leave the others standing. Both writes UPDATE the pair's existing
 * judgment — neither inserts, neither deletes it — so `restore` genuinely returns the pair
 * to its prior state.
 *
 * Does NOT refetch and does NOT patch anything: the caller owns the list it renders.
 */

import { useCallback, useRef, useState } from 'react';
import {
  DeleteOppAssetDismissalCommand,
  getIvtClient,
  PutOppAssetDismissalCommand,
} from '../../client/IvtHubClient';
import { DENIAL_HINTS, type DenialKind, denialKindOf } from '../../utils/authorization';

/**
 * Why a write failed. Three kinds rather than one message, because the caller's next move
 * differs and a string cannot carry the difference:
 *   - `not-judged` — 404. No relevance row exists for the pair, so there is nothing to
 *     dismiss: the page is showing a suggestion the server no longer has. Reload, do not
 *     retry.
 *   - `denied` — 403. A missing role, not a fault; `denial` names which group to ask for.
 *     Never offer a retry.
 *   - `failed` — everything else. The ONLY kind a retry button belongs on.
 */
export type DismissalFailureKind = 'not-judged' | 'denied' | 'failed';

export interface DismissalFailure {
  kind: DismissalFailureKind;
  /** Non-null exactly when `kind === 'denied'`. */
  denial: DenialKind | null;
  /** Ready to render; zh, matching the rest of this page. */
  message: string;
}

export interface DismissalState {
  /** True while this PAIR has a write in flight. Other pairs are unaffected. */
  isPending: (opptyId: string, assetId: string) => boolean;
  /** The pair's last failure, or null. Dropped when that pair's next write starts. */
  failureOf: (opptyId: string, assetId: string) => DismissalFailure | null;
  /** Mark the suggestion irrelevant. Resolves to null on success. */
  dismiss: (opptyId: string, assetId: string) => Promise<DismissalFailure | null>;
  /** Undo a dismissal. Resolves to null on success. */
  restore: (opptyId: string, assetId: string) => Promise<DismissalFailure | null>;
}

/**
 * One (opportunity, asset) pair as a string. Exported so a caller keeping its own per-pair state
 * keys it the same way. `\u0000` separator: neither an 18-char SFDC id nor a kebab-case asset slug
 * can contain it.
 */
export const dismissalKey = (opptyId: string, assetId: string) => `${opptyId}\u0000${assetId}`;
const keyOf = dismissalKey;

function classify(e: unknown): DismissalFailure {
  // Denial FIRST, and before the message is flattened: `denialKindOf` reads `reason`,
  // `name` and `$metadata.httpStatusCode` off the thrown object, none of which survive
  // `(e as Error).message`.
  const denial = denialKindOf(e);
  if (denial !== null) return { kind: 'denied', denial, message: DENIAL_HINTS[denial] };
  if ((e as { name?: string } | null)?.name === 'ResourceNotFoundException') {
    // The op refuses to invent a row, because that would fabricate a relevance score
    // nothing produced. An industry denial on a by-id write also lands here by design
    // ("exists but forbidden" is deliberately indistinguishable from "missing"), which is
    // why the copy describes the view rather than naming a cause.
    return {
      kind: 'not-judged',
      denial: null,
      message: '该资产推荐已不存在（页面数据可能已过期），请刷新页面。',
    };
  }
  const message = (e as Error | null)?.message;
  return {
    kind: 'failed',
    denial: null,
    message: message ? `操作失败，请重试。（${message}）` : '操作失败，请重试。',
  };
}

export function useDismissal(): DismissalState {
  const [state, setState] = useState<{
    pending: ReadonlySet<string>;
    failures: ReadonlyMap<string, DismissalFailure>;
  }>({ pending: new Set(), failures: new Map() });

  // Ref rather than the rendered `pending` set: a state updater is not guaranteed to run
  // synchronously at dispatch time, so reading the guard out of state can miss a
  // double-fire and send the write twice.
  const inFlight = useRef<Set<string>>(new Set());

  const run = useCallback(
    async (
      opptyId: string,
      assetId: string,
      send: () => Promise<unknown>,
    ): Promise<DismissalFailure | null> => {
      const key = keyOf(opptyId, assetId);
      // Already in flight for this pair. Not a failure the user should be told about — the
      // row's button is disabled while pending.
      if (inFlight.current.has(key)) return null;
      inFlight.current.add(key);

      setState((s) => {
        const pending = new Set(s.pending);
        pending.add(key);
        const failures = new Map(s.failures);
        failures.delete(key);
        return { pending, failures };
      });

      try {
        await send();
        return null;
      } catch (e) {
        const failure = classify(e);
        setState((s) => {
          const failures = new Map(s.failures);
          failures.set(key, failure);
          return { ...s, failures };
        });
        return failure;
      } finally {
        inFlight.current.delete(key);
        setState((s) => {
          if (!s.pending.has(key)) return s;
          const pending = new Set(s.pending);
          pending.delete(key);
          return { ...s, pending };
        });
      }
    },
    [],
  );

  const dismiss = useCallback(
    (opptyId: string, assetId: string) =>
      run(opptyId, assetId, () =>
        getIvtClient().send(new PutOppAssetDismissalCommand({ opptyId, assetId })),
      ),
    [run],
  );

  const restore = useCallback(
    (opptyId: string, assetId: string) =>
      run(opptyId, assetId, () =>
        getIvtClient().send(new DeleteOppAssetDismissalCommand({ opptyId, assetId })),
      ),
    [run],
  );

  const isPending = useCallback(
    (opptyId: string, assetId: string) => state.pending.has(keyOf(opptyId, assetId)),
    [state.pending],
  );

  const failureOf = useCallback(
    (opptyId: string, assetId: string) =>
      state.failures.get(keyOf(opptyId, assetId)) ?? null,
    [state.failures],
  );

  return { isPending, failureOf, dismiss, restore };
}
